/* eslint-disable @next/next/no-img-element */
import { JSX } from 'react'
import styled from 'styled-components'
import { isLocaleInLocaleList, getAssetUrl } from 'src/utils/helpers/helpers'
import { ISectionComponentFields } from 'contentful/types/contentful-modules'
import { Brand } from '@awaze/design-system.components.awaze-theme-provider'
import { useIntl } from 'react-intl'

const assets = {
  en: {
    ios: 'Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg',
    android: 'GooglePlay_English.svg',
  },
  hr: {
    ios: 'Download_on_the_App_Store_Badge_HR_blk_082124.svg',
    android: 'GooglePlay_Croatian.svg',
  },
  da: {
    ios: 'Download_on_the_App_Store_Badge_DK_RGB_blk_100217.svg',
    android: 'GooglePlay_Danish.svg',
  },
  'nl-BE': {
    ios: 'Download_on_the_App_Store_Badge_NL_RGB_blk_100317.svg',
    android: 'GooglePlay_Dutch.svg',
  },
  'nl-NL': {
    ios: 'Download_on_the_App_Store_Badge_NL_RGB_blk_100317.svg',
    android: 'GooglePlay_Dutch.svg',
  },
  'en-GB': {
    ios: 'Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg',
    android: 'GooglePlay_English.svg',
  },
  'en-US': {
    ios: 'Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg',
    android: 'GooglePlay_English.svg',
  },
  fr: {
    ios: 'Download_on_the_App_Store_Badge_FR_RGB_blk_100517.svg',
    android: 'GooglePlay_French.svg',
  },
  de: {
    ios: 'Download_on_the_App_Store_Badge_DE_RGB_blk_092917.svg',
    android: 'GooglePlay_German.svg',
  },
  'de-AT': {
    ios: 'Download_on_the_App_Store_Badge_DE_RGB_blk_092917.svg',
    android: 'GooglePlay_German.svg',
  },
  'de-DE': {
    ios: 'Download_on_the_App_Store_Badge_DE_RGB_blk_092917.svg',
    android: 'GooglePlay_German.svg',
  },
  'de-CH': {
    ios: 'Download_on_the_App_Store_Badge_DE_RGB_blk_092917.svg',
    android: 'GooglePlay_German.svg',
  },
  it: {
    ios: 'Download_on_the_App_Store_Badge_IT_RGB_blk_100317.svg',
    android: 'GooglePlay_Italian.svg',
  },
  nb: {
    ios: 'Download_on_the_App_Store_Badge_NO_RGB_blk_100317.svg',
    android: 'GooglePlay_Norwegian.svg',
  },
  pl: {
    ios: 'Download_on_the_App_Store_Badge_PL_RGB_blk_100317.svg',
    android: 'GooglePlay_Polish.svg',
  },
  es: {
    ios: 'Download_on_the_App_Store_Badge_ES_RGB_blk_100217.svg',
    android: 'GooglePlay_Spanish.svg',
  },
  sv: {
    ios: 'Download_on_the_App_Store_Badge_SE_RGB_blk_100317.svg',
    android: 'GooglePlay_Swedish.svg',
  },
}

const downloadLink = {
  novasol: {
    ios: 'https://apps.apple.com/app/novasol-holiday-home-rental/id6739567527',
    android: 'https://play.google.com/store/apps/details?id=com.novasol.guest',
  },
  'cottages.com': {
    ios: 'https://apps.apple.com/gb/app/cottages-com-holiday-cottages/id6741528848',
    android: 'https://play.google.com/store/apps/details?id=com.cottages.guest',
  },
  hoseasons: {
    ios: 'https://apps.apple.com/gb/app/hoseasons-book-uk-holidays/id6740243421',
    android:
      'https://play.google.com/store/apps/details?id=com.hoseasons.guest',
  },
  jamesVillas: {
    ios: 'https://apps.apple.com/gb/app/james-villas/id6754972184',
    android:
      'https://play.google.com/store/apps/details?id=com.jamesvillas.guest',
  },
}

const GetAppWrapper = styled.div<{
  section: ISectionComponentFields | undefined
}>`
  padding: ${({ section }) => (section ? '0px 0px' : '40px 0px')};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 16px;

  @media (max-width: 325px) {
    flex-direction: column;
  }
`

export interface GetAppModuleProps {
  brand: Brand | undefined
  locale: string | undefined
  section: ISectionComponentFields | undefined
  showInLocale: string[]
}

export const GetApp: React.FC<GetAppModuleProps> = ({
  brand,
  locale,
  section,
  showInLocale,
}): JSX.Element => {
  const { formatMessage } = useIntl()

  if (locale === undefined || brand === undefined) {
    return
  }

  const iosAsset = getAssetUrl(`/images/getApp/ios/${assets[locale]?.ios}`)
  const androidAsset = getAssetUrl(
    `/images/getApp/android/${assets[locale]?.android}`,
  )

  // If the locale is not in the showInLocale array, return null
  if (!isLocaleInLocaleList(locale, showInLocale)) {
    return
  }

  return (
    <GetAppWrapper section={section}>
      <a
        href={downloadLink[brand]?.ios ?? ''}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          data-testid="getapp-ios"
          loading="lazy"
          src={iosAsset}
          alt={formatMessage({
            id: 'get-app.alt.ios',
            defaultMessage: 'Download in the App Store',
          })}
          height={48}
        />
      </a>
      <a
        href={downloadLink[brand]?.android ?? ''}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          data-testid="getapp-android"
          loading="lazy"
          src={androidAsset}
          alt={formatMessage({
            id: 'get-app.alt.android',
            defaultMessage: 'Download in the Google Play store',
          })}
          height={48}
        />
      </a>
    </GetAppWrapper>
  )
}
